import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

// Types
export interface Course {
  id: number
  title: string
  description: string
  slug: string
  type: 'regular' | 'masterclass' | 'workshop' | 'trail'
  accessType: 'free' | 'restricted' | 'paid'
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  category?: string
  companyId?: string | null
  company?: { id: string; name: string } | null
  mentorId?: string | null
  mentor?: {
    id: string
    name: string
    photo: string | null
    role: string | null
    company: string | null
    bio: string | null
    linkedin: string | null
    instagram: string | null
  } | null
  duration?: number
  thumbnail?: string
  trailer?: string
  order: number
  status: 'draft' | 'published' | 'archived' | 'coming_soon'
  publishedAt?: string
  createdAt: string
  updatedAt: string
  // Detalhe dos módulos vem de /modules; aqui só usamos count.
  modules?: unknown[]
}

export interface CreateCourseDTO {
  title: string
  description: string
  slug: string
  type?: 'regular' | 'masterclass' | 'workshop' | 'trail'
  accessType?: 'free' | 'restricted' | 'paid'
  level?: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  category?: string
  duration?: number
  thumbnail?: string
  trailer?: string
  order?: number
  status?: 'draft' | 'published' | 'archived' | 'coming_soon'
  publishedAt?: string
  companyId?: string | null
  mentorId?: string | null
}

export interface UpdateCourseDTO {
  title?: string
  description?: string
  slug?: string
  type?: 'regular' | 'masterclass' | 'workshop' | 'trail'
  accessType?: 'free' | 'restricted' | 'paid'
  level?: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  category?: string
  duration?: number
  thumbnail?: string
  trailer?: string
  order?: number
  status?: 'draft' | 'published' | 'archived' | 'coming_soon'
  publishedAt?: string
  companyId?: string | null
  mentorId?: string | null
}

// Hooks
export function useAdminCourses(filters?: { status?: string }) {
  return useQuery({
    queryKey: ['admin-courses', filters],
    queryFn: async () => {
      const params = filters?.status ? { status: filters.status } : {}
      const response = await api.get<Course[]>('/admin/courses', { params })
      return response.data
    },
  })
}

export function useCreateCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateCourseDTO) => {
      const response = await api.post<Course>('/courses', data)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-courses'] })
      queryClient.refetchQueries({ queryKey: ['admin-courses'] })
    },
  })
}

export function useUpdateCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateCourseDTO }) => {
      const response = await api.put<Course>(`/courses/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-courses'] })
      queryClient.refetchQueries({ queryKey: ['admin-courses'] })
    },
  })
}

export function useDeleteCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/courses/${id}`)
    },
    onSuccess: () => {
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ['admin-courses'] })
      queryClient.refetchQueries({ queryKey: ['admin-courses'] })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Cadastro em massa — matricula vários usuários num curso de uma só vez.

/** Resumo devolvido por POST /admin/courses/:id/enroll-batch. */
export interface BulkEnrollResult {
  enrolled: number // matrículas efetivamente criadas
  alreadyEnrolled: number // já estavam no curso
  skipped: number // usuário inexistente ou de outra empresa
}

/** Linha de course_enrollments — aqui só interessa o userId. */
interface CourseEnrollmentRow {
  userId: string
}

/**
 * Lista quem já está matriculado num curso (GET /courses/:id/enrollments).
 * Usado pelo Cadastro em Massa pra não re-oferecer quem já está dentro.
 */
export function useCourseEnrollments(courseId: number | null) {
  return useQuery({
    queryKey: ['course-enrollments', courseId],
    queryFn: async () => {
      const response = await api.get<CourseEnrollmentRow[]>(`/courses/${courseId}/enrollments`)
      return response.data
    },
    enabled: !!courseId,
  })
}

export function useBulkEnroll() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ courseId, userIds }: { courseId: number; userIds: string[] }) => {
      const response = await api.post<BulkEnrollResult>(`/admin/courses/${courseId}/enroll-batch`, {
        userIds,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['course-enrollments'] })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Descadastro em massa — inverso do enroll-batch. Back responde com counts
// tolerantes (notEnrolled e skipped não derrubam o lote).

/** Resumo devolvido por POST /admin/courses/:id/unenroll-batch. */
export interface BulkUnenrollResult {
  unenrolled: number // matrículas efetivamente removidas
  notEnrolled: number // user existe mas não estava matriculado
  skipped: number // userId inexistente no banco
}

export function useBulkUnenroll() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ courseId, userIds }: { courseId: number; userIds: string[] }) => {
      const response = await api.post<BulkUnenrollResult>(
        `/admin/courses/${courseId}/unenroll-batch`,
        { userIds }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['course-enrollments'] })
      queryClient.invalidateQueries({ queryKey: ['user-enrollments'] })
    },
  })
}

export function useUpdateCourseStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: number
      status: 'draft' | 'published' | 'archived' | 'coming_soon'
    }) => {
      const response = await api.patch<Course>(`/courses/${id}/status`, { status })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses'] })
    },
  })
}
